import {
	Box,
	Button,
	Dialog,
	DialogContent,
	IconButton,
	SwipeableDrawer,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { transformFiType } from '../../hepler/functions';
import { useTranslation } from '../../hooks/useTranslation';

const BULLET_ORDER = ['SUMMARY', 'TRANSACTIONS', 'PROFILE'];

const getAccountTypesLabel = (consent, fallback) => {
	const fiTypes = consent?.fiTypes;
	if (!fiTypes?.length) return fallback;

	const labels = [...new Set(fiTypes.map((fi) => transformFiType(fi)))];
	if (labels.length === 1) return `${labels[0]} Accounts`;
	if (labels.length === 2) return `${labels[0]} and ${labels[1]} Accounts`;
	return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]} Accounts`;
};

const getConsentForSharing = (consentDetails) => {
	if (Array.isArray(consentDetails) && consentDetails.length > 0) {
		return consentDetails[0];
	}
	if (consentDetails && !Array.isArray(consentDetails)) {
		return consentDetails;
	}
	if (Array.isArray(global.consentList) && global.consentList.length > 0) {
		return global.consentList[0];
	}
	return global.consent || {};
};

function SharingDetailsContent({ consentDetails, customDetails, isDark, onClose, t, theme }) {
	const consent = getConsentForSharing(consentDetails);
	const entityName = global.entityName || 'the institution';
	const secondaryTextColor =
		customDetails?.secondaryTextColor ?? theme.palette.text.secondary;
	const accountTypes = getAccountTypesLabel(
		consent,
		t('accountDiscovery.sharingDetails.defaultAccountTypes'),
	);

	const bulletKeys = {
		SUMMARY: 'accountDiscovery.sharingDetails.latestBalance',
		TRANSACTIONS: 'accountDiscovery.sharingDetails.transactionsForPeriod',
		PROFILE: 'accountDiscovery.sharingDetails.profileInformation',
	};

	const activeTypes = consent?.consentTypes?.length
		? BULLET_ORDER.filter((type) => consent.consentTypes.includes(type))
		: BULLET_ORDER;

	const bullets = activeTypes.map((type) => t(bulletKeys[type]));

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
				fontFamily: customDetails?.fontFamily,
			}}
		>
			<Box sx={{ p: { xs: '1.5rem 1rem', md: '1.5rem' } }}>
				<HelpOutlineIcon
					sx={{
						fontSize: '1.5rem',
						color: isDark ? '#FFFFFF' : '#1A1A1A',
						mb: '0.75rem',
					}}
				/>

				<Typography
					sx={{
						fontWeight: 700,
						fontSize: '1.125rem',
						color: isDark ? '#FFFFFF' : '#1A1A1A',
						mb: '1rem',
						lineHeight: 1.3,
					}}
				>
					{t('accountDiscovery.sharingDetails.title')}
				</Typography>

				<Typography
					sx={{
						fontSize: '0.875rem',
						color: secondaryTextColor,
						lineHeight: 1.6,
						mb: '0.75rem',
					}}
				>
					{t('accountDiscovery.sharingDetails.description')
						.replace('{entityName}', entityName)
						.replace('{accountTypes}', accountTypes)}
				</Typography>

				<Box
					component="ul"
					sx={{
						m: 0,
						pl: '1.25rem',
						color: secondaryTextColor,
						fontSize: '0.875rem',
						lineHeight: 1.6,
						'& li': { mb: '0.25rem' },
					}}
				>
					{bullets.map((item) => (
						<li key={item}>{item}</li>
					))}
				</Box>

				<Button
					variant="outlined"
					fullWidth
					onClick={onClose}
					sx={{
						mt: '1.5rem',
						textTransform: 'none',
						boxShadow: 'none',
						borderColor: customDetails?.primaryColor || '#4A5EAD',
						color: customDetails?.primaryColor || '#4A5EAD',
						backgroundColor: isDark ? 'transparent' : '#FFFFFF',
						fontWeight: 500,
						fontSize: '1rem',
						py: '0.75rem',
						borderRadius: '0.5rem',
						'&:hover': {
							boxShadow: 'none',
							borderColor: customDetails?.primaryColor || '#4A5EAD',
							backgroundColor: isDark
								? 'rgba(255, 255, 255, 0.06)'
								: 'rgba(0, 0, 0, 0.04)',
						},
					}}
				>
					{t('consentRenewal.buttons.understood')}
				</Button>
			</Box>
		</Box>
	);
}

/**
 * Responsive sharing details modal.
 * Mobile → bottom SwipeableDrawer
 * Desktop → centered Dialog
 */
export default function SharingDetailsModal({
	open,
	onClose,
	customDetails,
	consentDetails,
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const sharedContent = (
		<SharingDetailsContent
			consentDetails={consentDetails}
			customDetails={customDetails}
			isDark={isDark}
			onClose={onClose}
			t={t}
			theme={theme}
		/>
	);

	const closeButton = (
		<IconButton
			onClick={onClose}
			aria-label={t('common.buttons.close')}
			sx={{
				position: 'absolute',
				top: '0.75rem',
				right: '0.75rem',
				zIndex: 1,
				color: isDark ? '#FFFFFF' : theme.palette.primary.main,
			}}
		>
			<Close />
		</IconButton>
	);

	const modalBody = (
		<Box sx={{ position: 'relative' }}>
			{closeButton}
			{sharedContent}
		</Box>
	);

	if (isMobile) {
		return (
			<SwipeableDrawer
				anchor="bottom"
				open={open}
				onOpen={() => {}}
				onClose={onClose}
				disableSwipeToOpen
				PaperProps={{
					sx: {
						borderRadius: '1rem 1rem 0 0',
						backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
					},
				}}
			>
				{modalBody}
			</SwipeableDrawer>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '1rem',
					overflow: 'hidden',
					backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
				},
			}}
		>
			<DialogContent sx={{ p: 0 }}>{modalBody}</DialogContent>
		</Dialog>
	);
}
